import { findIndex } from "lodash";
import React from "react";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { ImageGallery, SimpleModal, SimplePagination } from "src/app/components";
import { PixabayTransport } from "src/app/transports/pixabay";
import { ImageContent } from "src/app/types/content";
import { Order } from "src/app/types/orders/order";
import { IHits, IPixabay } from "src/app/types/pixabay";

const MAX_NUMBER_OF_SELECTED_IMAGES = 3;

export interface ISearchImageFormContainer {
    entity?: Order;
    disabled?: boolean;
    handleImageInsertion?(images: Array<IHits | ImageContent>): void;
    title?: string;
}
const SearchImageFormContainer = (props: ISearchImageFormContainer) => {
    const { entity, disabled = false, title, handleImageInsertion = () => {} } = props;
    const [currentSelectedImages, setCurrentSelectedImages] = useState<Array<IHits | ImageContent>>(
        entity?.content?.images || []
    );

    const [pixabayResult, setPixabayResult] = useState<IPixabay>();
    const [showModal, setShowModal] = useState(false);
    const [activePagination, setActivePagination] = useState(1);
    const [search, setSearch] = useState(entity?.thematic.name ? entity?.thematic.name : "");
    const { formatMessage } = useIntl();

    const handleSelectedImages = (image: IHits | ImageContent) => {
        let auxSelectedImage = currentSelectedImages;

        const index = findIndex(currentSelectedImages, (img) => img.id === image.id);

        if (index != -1) {
            auxSelectedImage = currentSelectedImages.filter(
                (el) => el.largeImageUrl !== image.largeImageUrl
            );
        } else {
            if (auxSelectedImage.length >= MAX_NUMBER_OF_SELECTED_IMAGES) return;
            auxSelectedImage.push(image);
        }

        setCurrentSelectedImages(() => [...auxSelectedImage]);
    };

    const handleCleanSelection = () => {
        setCurrentSelectedImages([]);
    };
    const nextPagination = () => {
        setActivePagination(activePagination + 1);
    };
    const prevPagination = () => {
        setActivePagination(activePagination - 1);
    };
    const changePagination = (number: number) => {
        setActivePagination(number);
    };

    const handleSearch = () => {
        PixabayTransport.list(search, activePagination)
            .then((res) => {
                const auxRes = { ...res, hits: res.hits.sort(() => 0.5 - Math.random()) };
                setPixabayResult(auxRes);
            })
            .catch(() => toast.error(formatMessage({ id: "common.notification.error.pixabay" })));
    };

    useEffect(() => {
        PixabayTransport.list(search, activePagination)
            .then((res) => {
                const auxRes = { ...res, hits: res.hits.sort(() => 0.5 - Math.random()) };
                setPixabayResult(auxRes);
            })
            .catch(() => toast.error(formatMessage({ id: "common.notification.error.pixabay" })));
    }, [activePagination]);

    const FooterSearchInput = (
        <div className="d-flex w-100 align-items-center justify-content-between">
            <div className="d-flex h-auto gap-2">
                <input
                    className="form-control"
                    placeholder="Search"
                    name="search"
                    defaultValue={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                />
                <Button onClick={handleSearch}>
                    {formatMessage({ id: "common.action.search" })}
                </Button>
                <Button onClick={handleCleanSelection}>
                    {formatMessage({ id: "common.action.clean" })}
                </Button>
            </div>
            <SimplePagination
                activePagination={activePagination}
                nextPagination={nextPagination}
                prevPagination={prevPagination}
                changePagination={changePagination}
                numberMaxOfPagination={Math.ceil((pixabayResult?.totalHits || 1) / 64)}
            />
            <div />
        </div>
    );
    const Header = (
        <div className="d-flex w-100 align-items-center justify-content-around">
            {title && (
                <div className="modal-title h4 d-flex">
                    <b>{formatMessage({ id: "common.field.title" })}: </b>
                    <span
                        style={{
                            display: "block",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            width: 500,
                            marginLeft: 8,
                        }}
                    >
                        {title}
                    </span>
                </div>
            )}
            {entity?.keywords && (
                <>
                    <div
                        className="thematic-react-tag-wrapper gap-2 pb-2 pt-2"
                        style={{ width: "auto" }}
                    >
                        <b className="modal-title h4">
                            {formatMessage({ id: "common.field.keywords" })}:{" "}
                        </b>
                        {entity?.keywords
                            ?.sort()
                            .map((keyword: string | undefined, index: number) => (
                                <React.Fragment key={index}>
                                    <div className="thematic-react-tag modal-title h4">
                                        {keyword}
                                    </div>
                                </React.Fragment>
                            ))}
                    </div>
                </>
            )}
        </div>
    );

    return (
        <>
            <Button disabled={disabled} onClick={() => setShowModal(true)}>
                {formatMessage({ id: "common.action.openImageSearch" })}
            </Button>
            <SimpleModal
                entityName={formatMessage({ id: "common.entity.SearchImages" })}
                loading={false}
                showModal={showModal}
                buttonConfirmationAction={() => {
                    handleImageInsertion(currentSelectedImages);
                    setShowModal(false);
                }}
                buttonConfirmationLabel={formatMessage({ id: "common.action.saveImages" })}
                handleClose={() => {
                    setShowModal(false);
                }}
                formId={""}
                modalProps={{ fullscreen: true }}
                FooterLeftComponent={FooterSearchInput}
                headerRightComponent={Header}
            >
                <ImageGallery
                    currentSelectedImages={currentSelectedImages}
                    imagesList={pixabayResult?.hits}
                    handleSelectedImages={handleSelectedImages}
                    asSideGallery
                />
            </SimpleModal>
        </>
    );
};

export default SearchImageFormContainer;
